import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, Phone, X, CheckCircle, XCircle, Clock, AlertCircle, Languages, Sparkles, Brain, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { config } from '@/src/config';
import { useFormattedDate } from '../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
import {
  detectLanguage,
  translateText,
  getSuggestedResponses,
  getLanguageFlag,
  getLanguageName,
  type Translation,
  type ConversationMessage
} from '@/services/api/aiCommunications';

interface SMSMessage {
  id: number;
  message_sid: string;
  from_phone: string;
  to_phone: string;
  message_body: string;
  direction: 'inbound' | 'outbound';
  intent?: string;
  status: string;
  num_segments: number;
  created_at: string;
  delivered_at?: string;
  // AI features
  detected_language?: string;
  translated_text?: string;
}

interface SMSConversationViewProps {
  referralId: string;
  providerId: string;
  providerName: string;
  providerPhone: string;
  onClose?: () => void;
}

const SMSConversationView: React.FC<SMSConversationViewProps> = ({
  referralId,
  providerId,
  providerName,
  providerPhone,
  onClose
}) => {
  const { fmt } = useFormattedDate();
  const [messages, setMessages] = useState<SMSMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // AI Communication features
  const [translationEnabled, setTranslationEnabled] = useState(false);
  const [conversationLanguage, setConversationLanguage] = useState<string>('en');
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    fetchConversation();
    // Poll for new messages every 10 seconds
    const interval = setInterval(fetchConversation, 10000);
    return () => clearInterval(interval);
  }, [referralId, providerId]);

  const fetchConversation = async () => {
    try {
      const response = await authFetch(`${config.api.baseUrl}/api/sms/conversations?referral_id=${referralId}&provider_id=${providerId}`);

      if (response.ok) {
        const data = await response.json();
        const fetchedMessages = data.messages || [];
        setMessages(fetchedMessages);

        // Auto-detect language from last inbound message
        const lastInbound = fetchedMessages.filter((m: SMSMessage) => m.direction === 'inbound').pop();
        if (lastInbound && !lastInbound.detected_language) {
          detectMessageLanguage(lastInbound);
        }
      }
    } catch (err) {
      console.error('Error fetching conversation:', err);
    } finally {
      setLoading(false);
    }
  };

  // Detect language of a message and optionally translate
  const detectMessageLanguage = async (message: SMSMessage) => {
    try {
      const detection = await detectLanguage(message.message_body);

      // Update message with detected language
      setMessages(prev => prev.map(m =>
        m.id === message.id
          ? { ...m, detected_language: detection.language }
          : m
      ));

      // Auto-enable translation if not English
      if (detection.language !== 'en' && detection.confidence > 0.7) {
        setConversationLanguage(detection.language);
        setTranslationEnabled(true);

        // Auto-translate the message
        const translation = await translateText(message.message_body, 'en', detection.language);
        setMessages(prev => prev.map(m =>
          m.id === message.id
            ? { ...m, translated_text: translation.translated_text }
            : m
        ));
      }
    } catch (err) {
      console.error('Error detecting language:', err);
    }
  };

  // Fetch AI-suggested responses based on conversation
  const fetchAISuggestions = async () => {
    if (messages.length === 0) return;

    setLoadingSuggestions(true);
    try {
      // Build conversation history for AI
      const conversationHistory: ConversationMessage[] = messages.slice(-5).map(msg => ({
        role: msg.direction === 'inbound' ? 'user' : 'assistant',
        content: msg.message_body
      }));

      const suggestions = await getSuggestedResponses(
        conversationHistory,
        'healthcare care coordination',
        conversationLanguage
      );

      setAiSuggestions(suggestions.suggestions);
      setShowSuggestions(true);
    } catch (err) {
      console.error('Error fetching AI suggestions:', err);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      setError(null);

      const response = await authFetch(`${config.api.baseUrl}/api/sms/send`, {
        method: 'POST',
        jsonBody: {
          referral_id: referralId,
          provider_id: providerId,
          to_phone: providerPhone,
          message_text: newMessage
        }
      });

      if (response.ok) {
        const result = await response.json();
        setNewMessage('');
        // Add optimistic update
        setMessages(prev => [...prev, {
          id: Date.now(),
          message_sid: result.message_sid || '',
          from_phone: result.from_phone || '',
          to_phone: providerPhone,
          message_body: newMessage,
          direction: 'outbound',
          status: 'queued',
          num_segments: Math.ceil(newMessage.length / 160),
          created_at: new Date().toISOString()
        }]);
        // Fetch to get actual message
        setTimeout(fetchConversation, 1000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to send message');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle className="w-3 h-3 text-green-600" />;
      case 'failed':
      case 'undelivered':
        return <XCircle className="w-3 h-3 text-red-600" />;
      case 'sent':
        return <CheckCircle className="w-3 h-3 text-gray-400" />;
      default:
        return <Clock className="w-3 h-3 text-gray-400" />;
    }
  };

  const getIntentBadge = (intent?: string) => {
    if (!intent || intent === 'unknown') return null;

    const colors = {
      accept: 'bg-green-100 text-green-800',
      decline: 'bg-red-100 text-red-800',
      question: 'bg-blue-100 text-blue-800',
      help: 'bg-purple-100 text-purple-800',
      stop: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colors[intent] || 'bg-gray-100 text-gray-600'}`}>
        {intent.charAt(0).toUpperCase() + intent.slice(1)}
      </span>
    );
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return fmt.time(timestamp);
    } else if (days === 1) {
      return 'Yesterday ' + fmt.time(timestamp);
    } else {
      return fmt.medium(timestamp) + ' ' + fmt.time(timestamp);
    }
  };

  const characterCount = newMessage.length;
  const segmentCount = Math.ceil(characterCount / 160);
  const charactersRemaining = (segmentCount * 160) - characterCount;

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-600 rounded-full p-2">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg">SMS Conversation</CardTitle>
              <div className="flex items-center space-x-2 mt-1">
                <Phone className="w-3 h-3 text-gray-500" />
                <span className="text-sm text-gray-600">{providerName}</span>
                <span className="text-sm text-gray-400">•</span>
                <span className="text-sm text-gray-500">{providerPhone}</span>
                {conversationLanguage !== 'en' && (
                  <>
                    <span className="text-sm text-gray-400">•</span>
                    <span className="text-sm text-gray-600 flex items-center">
                      {getLanguageFlag(conversationLanguage)} {getLanguageName(conversationLanguage)}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {conversationLanguage !== 'en' && (
              <button
                onClick={() => setTranslationEnabled(!translationEnabled)}
                className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  translationEnabled
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                }`}
                title={translationEnabled ? 'Hide translations' : 'Show translations'}
              >
                <Languages className="w-4 h-4" />
                <span>{translationEnabled ? 'On' : 'Off'}</span>
              </button>
            )}
            {onClose && (
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Messages Container */}
        <div className="h-96 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-500">Loading conversation...</div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <MessageCircle className="w-12 h-12 mb-2 text-gray-300" />
              <p>No messages yet</p>
              <p className="text-sm">Start the conversation below</p>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[70%] ${message.direction === 'outbound' ? 'order-2' : 'order-1'}`}>
                  {/* Language Badge (for inbound non-English messages) */}
                  {message.direction === 'inbound' && message.detected_language && message.detected_language !== 'en' && (
                    <div className="flex items-center space-x-1 mb-1">
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full flex items-center">
                        <Globe className="w-3 h-3 mr-1" />
                        {getLanguageFlag(message.detected_language)} {getLanguageName(message.detected_language)}
                      </span>
                    </div>
                  )}

                  {/* Message Bubble */}
                  <div
                    className={`rounded-lg p-3 ${
                      message.direction === 'outbound'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-900 border border-gray-200'
                    }`}
                  >
                    {/* Original Text */}
                    <p className="text-sm whitespace-pre-wrap break-words">{message.message_body}</p>

                    {/* Translated Text (if available and translation enabled) */}
                    {translationEnabled && message.translated_text && message.detected_language !== 'en' && (
                      <div className={`mt-2 pt-2 border-t ${
                        message.direction === 'outbound' ? 'border-blue-400' : 'border-gray-200'
                      }`}>
                        <div className="flex items-center space-x-1 mb-1">
                          <Languages className="w-3 h-3 opacity-70" />
                          <span className="text-xs opacity-70">🇺🇸 English</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap break-words opacity-90 italic">
                          {message.translated_text}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Message Metadata */}
                  <div className={`flex items-center space-x-2 mt-1 ${
                    message.direction === 'outbound' ? 'justify-end' : 'justify-start'
                  }`}>
                    <span className="text-xs text-gray-500">{formatTime(message.created_at)}</span>
                    {message.direction === 'outbound' && getStatusIcon(message.status)}
                    {message.num_segments > 1 && (
                      <span className="text-xs text-gray-400">({message.num_segments} parts)</span>
                    )}
                    {message.direction === 'inbound' && message.intent && getIntentBadge(message.intent)}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t bg-white p-4">
          {error && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded flex items-center space-x-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          )}

          {/* AI Suggestions */}
          {showSuggestions && aiSuggestions.length > 0 && (
            <div className="mb-3 p-3 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-900">AI-Suggested Responses</span>
                </div>
                <button
                  onClick={() => setShowSuggestions(false)}
                  className="text-purple-600 hover:text-purple-800 text-xs"
                >
                  Dismiss
                </button>
              </div>
              <div className="space-y-2">
                {aiSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setNewMessage(suggestion);
                      setShowSuggestions(false);
                    }}
                    className="w-full text-left p-2 bg-white hover:bg-purple-50 border border-purple-200 rounded text-sm transition-colors flex items-start space-x-2"
                  >
                    <Brain className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{suggestion}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-end space-x-2">
            <div className="flex-1">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="w-full border border-gray-300 rounded-lg p-3 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                disabled={sending}
              />
              <div className="flex items-center justify-between mt-1 px-1">
                <span className={`text-xs ${
                  segmentCount > 1 ? 'text-orange-600 font-medium' : 'text-gray-500'
                }`}>
                  {characterCount} chars • {segmentCount} SMS {segmentCount > 1 ? `(${charactersRemaining} until next segment)` : ''}
                </span>
                <div className="flex items-center space-x-2">
                  {segmentCount > 3 && (
                    <span className="text-xs text-red-600">⚠️ Long message</span>
                  )}
                  <button
                    onClick={fetchAISuggestions}
                    disabled={loadingSuggestions || messages.length === 0}
                    className="flex items-center space-x-1 px-2 py-1 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Get AI-suggested responses"
                  >
                    {loadingSuggestions ? (
                      <>
                        <Clock className="w-3 h-3 animate-spin" />
                        <span>Thinking...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3" />
                        <span>AI Suggest</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
            <Button
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || sending}
              className="bg-v2accent hover:bg-v2accent/90 text-white px-6 py-3 h-auto"
            >
              {sending ? (
                <Clock className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Send
                </>
              )}
            </Button>
          </div>

          <div className="mt-3 text-xs text-gray-500 flex items-start space-x-1">
            <AlertCircle className="w-3 h-3 mt-0.5" />
            <span>
              Press Enter to send, Shift+Enter for new line. Keep messages under 160 characters to avoid extra charges.
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SMSConversationView;
